import { InfoCard } from '#app/components/ui/info-card.tsx'
import { type Route } from './+types/awards.ts'

type QuickLinkSection = {
	id: string
	label: string
	cards: Array<{ id: string; label: string }>
}

const QUICK_LINK_SECTIONS: QuickLinkSection[] = [
	{
		id: 'adjudication-awards',
		label: 'Adjudication Awards',
		cards: [
			{ id: 'adjudication-overview', label: 'Adjudication Overview' },
			{ id: 'adjudication-table', label: 'Adjudication Points' },
		],
	},
	{
		id: 'solo-overall-awards',
		label: 'Solo Overall Awards',
		cards: [
			{ id: 'solo-10-14-runner-up', label: '10th - 14th Runner Up' },
			{ id: 'solo-1-9-runner-up', label: '1st - 9th Runner Up' },
			{ id: 'solo-high-score', label: 'High Score' },
		],
	},
	{
		id: 'duo-trio-overall-awards',
		label: 'Duo/Trio Overall Awards',
		cards: [
			{ id: 'duo-style-high-score', label: 'Style High Score' },
			{ id: 'duo-judges-pick', label: "Judges' Pick" },
			{ id: 'duo-directors-pick', label: "Directors' Pick" },
			{
				id: 'duo-age-division-2-3',
				label: 'Age Division Duo/Trio 2nd - 3rd Place',
			},
			{
				id: 'duo-age-division-high-score',
				label: 'Age Division Duo/Trio High Score',
			},
			{ id: 'duo-overall-high-score', label: 'Overall Duo/Trio High Score' },
		],
	},
	{
		id: 'group-overall-awards',
		label: 'Group Overall Awards',
		cards: [
			{ id: 'group-style-high-score', label: 'Style High Score' },
			{ id: 'group-danceagain', label: 'DanceAgain!' },
			{ id: 'group-judges-pick', label: "Judges' Pick" },
			{ id: 'group-directors-pick', label: "Directors' Pick" },
			{
				id: 'group-age-division-category-high-score',
				label: 'Age Division Category High Score',
			},
			{
				id: 'group-age-division-category-2-3',
				label: 'Age Division Category 2nd - 3rd Place',
			},
			{
				id: 'group-age-division-high-score',
				label: 'Age Division High Score',
			},
			{ id: 'group-overall-high-score', label: 'Overall High Score' },
		],
	},
	{
		id: 'studio-awards',
		label: 'Studio Awards',
		cards: [
			{ id: 'studio-shaffer', label: 'The Shaffer Studio' },
			{
				id: 'studio-best-choreography',
				label: 'Best Studio Choreography',
			},
			{ id: 'studio-standout', label: 'The Standout Studio' },
			{ id: 'studio-breakthrough', label: 'The Breakthrough Studio' },
			{ id: 'studio-weekend', label: 'Studio of the Weekend' },
		],
	},
	{ id: 'awards-questions', label: 'Questions About Awards?', cards: [] },
]

function QuickLinksPanel() {
	return (
		<div className="border border-muted-foreground/30 bg-muted/30 p-4 sm:p-6">
			<div className="mb-4 flex items-center gap-3 sm:mb-5">
				<div className="flex h-8 w-8 items-center justify-center text-primary">
					<svg
						className="h-5 w-5"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						strokeWidth={2.5}
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
						/>
					</svg>
				</div>
				<h3 className="font-display text-base font-bold tracking-wide sm:text-lg">
					QUICK LINKS
				</h3>
			</div>

			<div className="space-y-3">
				{QUICK_LINK_SECTIONS.map((section, sectionIndex) => (
					<div
						key={section.id}
						className="border border-border/50 bg-background/95 p-3"
					>
						<div className="mb-2 flex items-center gap-2">
							<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-foreground">
								{sectionIndex + 1}
							</span>
							<a
								href={`#${section.id}`}
								className="inline-flex flex-1 items-center border border-border/60 bg-muted/40 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-foreground transition-colors hover:border-primary/50 hover:text-primary"
							>
								<span>{section.label}</span>
							</a>
						</div>

						{section.cards.length > 0 ? (
							<div className="ml-2 space-y-1.5 border-l border-border/50 pl-3">
								{section.cards.map((card) => (
									<a
										key={card.id}
										href={`#${card.id}`}
										className="group flex items-start gap-2 rounded-sm border border-transparent px-2 py-1.5 text-xs leading-snug text-foreground/85 transition-all hover:border-border/60 hover:bg-muted/40 hover:text-foreground"
									>
										<span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary/60 transition-colors group-hover:bg-primary" />
										<span className="flex-1">{card.label}</span>
									</a>
								))}
							</div>
						) : (
							<p className="ml-2 text-xs text-muted-foreground">
								General section details.
							</p>
						)}
					</div>
				))}
			</div>
		</div>
	)
}

export const meta: Route.MetaFunction = () => [
	{ title: 'Awards | DanceMakers' },
	{
		name: 'description',
		content:
			'Learn about DanceMakers competition awards including adjudication awards, solo awards, duo/trio awards, group awards, and studio recognition.',
	},
]

export default function AwardsRoute() {
	return (
		<div className="min-h-screen pt-12 md:pt-16">
			{/* Hero Section */}
			<section className="container px-6 pb-8 pt-12 md:px-16 md:pb-10 md:pt-16">
				<div className="max-w-3xl">
					<p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
						2026-27 Season
					</p>
					<h1 className="mb-6 font-display text-3xl font-bold uppercase leading-tight md:text-4xl lg:text-5xl">
						Awards
					</h1>
					<p className="text-sm leading-relaxed text-muted-foreground">
						DanceMakers recognizes excellence through a comprehensive awards
						system. From adjudication scores to overall high scores, we
						celebrate outstanding performances across all categories and age
						divisions.
					</p>
				</div>
			</section>

			<div className="lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start lg:gap-8 lg:px-6 xl:px-12">
				<div className="min-w-0">
					{/* Adjudication Awards */}
					<section
						id="adjudication-awards"
						className="scroll-mt-36 bg-muted/30 py-8 md:py-12"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Adjudication Awards
							</h2>

							<div
								id="adjudication-overview"
								className="mb-6 max-w-4xl scroll-mt-36 space-y-3 text-xs leading-relaxed text-muted-foreground sm:mb-8 sm:space-y-4 sm:text-sm"
							>
								<p>
									<span className="font-medium text-foreground">
										All routines will be adjudicated by three of the DanceMakers
										judges.
									</span>
								</p>
								<p>
									Solo routines will receive an adjudication score in your
									registration portal, but will not be announced for an
									adjudication award.{' '}
									<span className="font-medium text-foreground">
										All soloist will receive a participation award.
									</span>
								</p>
								<p>
									Duo/Trio and Group routines will receive an adjudication score
									in your registration portal and will be announced during their
									designated awards ceremony.
								</p>
								<p className="font-medium text-foreground">
									All routines that score 80 points or higher qualify to compete
									at Nationals.
								</p>
								<p>
									If a routine performing was choreographed by one of the
									judges, that judge will only critique the routine and the
									score will be an average of the other two judges.
								</p>
							</div>

							<div
								id="adjudication-table"
								className="-mx-6 max-w-2xl scroll-mt-36 overflow-x-auto px-6 md:mx-0 md:px-0"
							>
								<table className="w-full border border-muted-foreground/30 text-xs sm:text-sm">
									<thead>
										<tr className="bg-muted/30">
											<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Adjudication
											</th>
											<th className="border-b border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Points
											</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												High 1st Place
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												96-100
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												1st Place
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												90-95
											</td>
										</tr>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												High 2nd Place
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												85-89
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												2nd Place
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												80-84
											</td>
										</tr>
										<tr>
											<td className="border-r border-muted-foreground/30 p-2 sm:p-4">
												3rd Place
											</td>
											<td className="p-2 font-medium sm:p-4">70-79</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</section>

					{/* Solo Overall Awards */}
					<section
						id="solo-overall-awards"
						className="scroll-mt-36 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Solo Overall Awards
							</h2>

							<div className="mb-4 grid max-w-6xl gap-4 sm:mb-6 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
								<div id="solo-10-14-runner-up" className="scroll-mt-36">
									<InfoCard title="10th - 14th Runner Up">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Recognition
											</span>
										</p>
										<p className="mb-3">
											Special recognition in each age division (Camp Dance,
											Junior, Pre-Teen, Teen or Senior).
										</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony on Sunday.
										</p>
									</InfoCard>
								</div>

								<div id="solo-1-9-runner-up" className="scroll-mt-36">
									<InfoCard title="1st - 9th Runner Up">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Plaque
											</span>
										</p>
										<p className="mb-3">
											Runner up placements in each age division (Camp Dance,
											Junior, Pre-Teen, Teen or Senior).
										</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony on Sunday.
										</p>
									</InfoCard>
								</div>

								<div id="solo-high-score" className="scroll-mt-36">
									<InfoCard title="High Score">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Plaque / Free Entry to Nationals
											</span>
										</p>
										<p className="mb-3">
											Highest scoring solo in each age division (Camp Dance,
											Junior, Pre-Teen, Teen or Senior).
										</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony on Sunday.
										</p>
									</InfoCard>
								</div>
							</div>

							<p className="max-w-4xl text-xs italic text-muted-foreground">
								* DanceMakers reserves the rights to extend placement awards
								depending on the number of registered routines.
							</p>
						</div>
					</section>

					{/* Duo/Trio Overall Awards */}
					<section
						id="duo-trio-overall-awards"
						className="scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Duo/Trio Overall Awards
							</h2>

							<div className="mb-4 grid max-w-6xl gap-4 sm:mb-6 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
								<div id="duo-style-high-score" className="scroll-mt-36">
									<InfoCard title="Style High Score">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Plaque
											</span>
										</p>
										<p className="mb-3">
											Duo/Trio High Score in each age division (Camp Dance,
											Junior, Pre- Teen, Teen or Senior) and style (Ballet, Tap,
											Jazz, Lyrical, Contemporary, Musical Theatre, Hip Hop or
											Open).
										</p>
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Example: "Junior Tap Duo/Trio High Score"
											</span>
										</p>
										<p className="text-xs">
											Announced at the routine's assigned awards ceremony on
											either Friday or Saturday evening.
										</p>
									</InfoCard>
								</div>

								<div id="duo-judges-pick" className="scroll-mt-36">
									<InfoCard title="Judges' Pick">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Plaque
											</span>
										</p>
										<p className="mb-3">
											Routines the judges pick for special recognition in each
											age division nondependent on scores.
										</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony.
										</p>
									</InfoCard>
								</div>

								<div id="duo-directors-pick" className="scroll-mt-36">
									<InfoCard title="Directors' Pick">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Plaque
											</span>
										</p>
										<p className="mb-3">
											Routines that the directors pick for special recognition
											in each age division nondependent on scores.
										</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony.
										</p>
									</InfoCard>
								</div>

								<div id="duo-age-division-2-3" className="scroll-mt-36">
									<InfoCard title="Age Division Duo/Trio 2nd - 3rd Place">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Plaque
											</span>
										</p>
										<p className="mb-3">
											Duo/Trio 2nd - 3rd Place in each age division (Camp Dance,
											Junior, Pre- Teen, Teen or Senior).
										</p>
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Example: "Junior Duo/Trio 2nd Place"
											</span>
										</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony on Sunday.
										</p>
									</InfoCard>
								</div>

								<div id="duo-age-division-high-score" className="scroll-mt-36">
									<InfoCard title="Age Division Duo/Trio High Score">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Acrylic Award / 50% off Routine Entry for Nationals
											</span>
										</p>
										<p className="mb-3">
											Duo/Trio High Score in each age division (Camp Dance,
											Junior, Pre- Teen, Teen or Senior) excluding Category High
											Scores.
										</p>
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Example: "Junior Duo/Trio High Score"
											</span>
										</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony on Sunday.
										</p>
									</InfoCard>
								</div>

								<div
									id="duo-overall-high-score"
									className="scroll-mt-36 md:col-span-2 lg:col-span-3"
								>
									<InfoCard title="Overall Duo/Trio High Score">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Acrylic Award / Free Entry to Nationals
											</span>
										</p>
										<p className="mb-3">Highest Scoring Duo/Trio.</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony on Sunday.
										</p>
									</InfoCard>
								</div>
							</div>

							<p className="max-w-4xl text-xs italic text-muted-foreground">
								* DanceMakers reserves the rights to extend placement awards
								depending on the number of registered routines.
							</p>
							<p className="mt-2 max-w-4xl text-xs italic text-muted-foreground">
								* A routine can only win 1 overall award. For example, if a Teen
								Jazz Duo/Trio wins Overall Duo/Trio High Score, it cannot also
								win Teen Duo/Trio High Score.
							</p>
						</div>
					</section>

					{/* Group Overall Awards */}
					<section
						id="group-overall-awards"
						className="scroll-mt-36 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Group Overall Awards
							</h2>

							<div className="mb-4 grid max-w-6xl gap-4 sm:mb-6 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
								<div id="group-style-high-score" className="scroll-mt-36">
									<InfoCard title="Style High Score">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Plaque
											</span>
										</p>
										<p className="mb-3">
											High Score in each age division (Camp Dance, Junior, Pre-
											Teen, Teen or Senior), category (Group, Line, Extended
											Line or Production) and style (Ballet, Tap, Jazz, Lyrical,
											Contemporary, Musical Theatre, Hip Hop or Open).
										</p>
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Example: "Junior Tap Group High Score"
											</span>
										</p>
										<p className="text-xs">
											Announced at the routine's assigned awards ceremony on
											either Friday or Saturday evening.
										</p>
									</InfoCard>
								</div>

								<div id="group-danceagain" className="scroll-mt-36">
									<InfoCard title="DanceAgain!">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Performance / Plaque
											</span>
										</p>
										<p className="mb-3">
											Judges choose their favorite routines to Dance Again at
											the Wrap It Ceremony on Sunday and are nondependent on
											scores.
										</p>
										<p className="text-xs font-medium text-foreground">
											These routines will perform LIVE during our Wrap It
											Ceremony on Sunday.
										</p>
									</InfoCard>
								</div>

								<div id="group-judges-pick" className="scroll-mt-36">
									<InfoCard title="Judges' Pick">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Plaque
											</span>
										</p>
										<p className="mb-3">
											Routines the judges pick for special recognition in each
											age division and category nondependent on scores.
										</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony.
										</p>
									</InfoCard>
								</div>

								<div id="group-directors-pick" className="scroll-mt-36">
									<InfoCard title="Directors' Pick">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Plaque
											</span>
										</p>
										<p className="mb-3">
											Routines that the directors pick for special recognition
											in each age division and category nondependent on scores.
										</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony.
										</p>
									</InfoCard>
								</div>

								<div
									id="group-age-division-category-high-score"
									className="scroll-mt-36"
								>
									<InfoCard title="Age Division Category High Score">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Plaque
											</span>
										</p>
										<p className="mb-3">
											High Score in each age division (Camp Dance, Junior, Pre-
											Teen, Teen or Senior) and category (Group, Line, Extended
											Line or Production).
										</p>
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Example: "Junior Group High Score"
											</span>
										</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony on Sunday.
										</p>
									</InfoCard>
								</div>

								<div
									id="group-age-division-category-2-3"
									className="scroll-mt-36"
								>
									<InfoCard title="Age Division Category 2nd - 3rd Place">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Plaque
											</span>
										</p>
										<p className="mb-3">
											2nd - 3rd Place in each age division (Camp Dance, Junior,
											Pre- Teen, Teen or Senior) and category (Group, Line,
											Extended Line or Production).
										</p>
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Example: "Junior Group 2nd Place"
											</span>
										</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony on Sunday.
										</p>
									</InfoCard>
								</div>

								<div
									id="group-age-division-high-score"
									className="scroll-mt-36"
								>
									<InfoCard title="Age Division High Score">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Acrylic Award / 50% off Routine Entry for Nationals
											</span>
										</p>
										<p className="mb-3">
											High Score in each age division (Camp Dance, Junior, Pre-
											Teen, Teen or Senior).
										</p>
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Example: "Junior High Score"
											</span>
										</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony on Sunday.
										</p>
									</InfoCard>
								</div>

								<div
									id="group-overall-high-score"
									className="scroll-mt-36 md:col-span-2 lg:col-span-3"
								>
									<InfoCard title="Overall High Score">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Acrylic Award / Free Entry to Nationals
											</span>
										</p>
										<p className="mb-3">
											Highest Score in all of group competition excluding
											Category High Scores and Age Division High Scores.
										</p>
										<p className="text-xs">
											Announced at the Wrap It Ceremony on Sunday.
										</p>
									</InfoCard>
								</div>
							</div>

							<p className="max-w-4xl text-xs italic text-muted-foreground">
								* DanceMakers reserves the rights to extend placement awards
								depending on the number of registered routines.
							</p>
							<p className="mt-2 max-w-4xl text-xs italic text-muted-foreground">
								* A routine can only win 1 overall award. For example, if a
								Senior Tap Line wins Overall High Score, it cannot also win
								Senior Line High Score.
							</p>
						</div>
					</section>

					{/* Studio Awards */}
					<section
						id="studio-awards"
						className="scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Studio Awards
							</h2>

							<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2">
								<div id="studio-shaffer" className="scroll-mt-36">
									<InfoCard title="The Shaffer Studio">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Acrylic Award / $100 Competition Voucher for Nationals
											</span>
										</p>
										<p>
											This will be awarded to the studio that shows{' '}
											<span className="font-medium text-foreground">
												support, encouragement, professionalism and shares their
												love for dance with others
											</span>{' '}
											throughout the event. This award is in honor of Mr. Doug
											Shaffer who has shared his love of dance with others for
											many years.
										</p>
									</InfoCard>
								</div>

								<div id="studio-best-choreography" className="scroll-mt-36">
									<InfoCard title="Best Studio Choreography">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Acrylic Award / $100 Competition Voucher for Nationals
											</span>
										</p>
										<p>
											This will be awarded to the studio that has the best
											overall choreography throughout competition.
										</p>
									</InfoCard>
								</div>

								<div id="studio-standout" className="scroll-mt-36">
									<InfoCard title="The Standout Studio">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Acrylic Award / $100 Competition Voucher for Nationals
											</span>
										</p>
										<p>
											This will be awarded to the studio that{' '}
											<span className="font-medium text-foreground">
												stands out in convention work and competition
												performance
											</span>
											.
										</p>
									</InfoCard>
								</div>

								<div id="studio-breakthrough" className="scroll-mt-36">
									<InfoCard title="The Breakthrough Studio">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Acrylic Award / $100 Competition Voucher for Nationals
											</span>
										</p>
										<p>
											This will be awarded to the studio that has the most
											overall improvement.
										</p>
									</InfoCard>
								</div>

								<div id="studio-weekend" className="scroll-mt-36 md:col-span-2">
									<InfoCard title="Studio of the Weekend">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Acrylic Award / $250 Competition Voucher for Nationals
											</span>
										</p>
										<p>
											This is awarded to the studio who embodies greatness in
											all aspects during the weekend! This includes, but is not
											limited to, competition, convention, and the in-between
											moments throughout the event. This studio represents the
											highest caliber in dance, along with being supportive and
											encouraging to other dancers. The Studio of the Weekend is
											to represent DanceMakers values and mission and is meant
											to inspire other studios throughout the country.
										</p>
									</InfoCard>
								</div>
							</div>

							<p className="mt-4 max-w-4xl text-xs italic text-muted-foreground">
								*A studio can only win one studio award a weekend.
							</p>
						</div>
					</section>

					{/* Contact CTA */}
					<section
						id="awards-questions"
						className="scroll-mt-36 py-10 md:py-16"
					>
						<div className="container px-6 text-center md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:text-2xl md:text-3xl">
								Questions About Awards?
							</h2>
							<p className="mx-auto mb-6 max-w-2xl text-sm text-muted-foreground">
								Have questions about our awards system or recognition programs?
								Our team is here to help!
							</p>
							<a
								href="/contact"
								className="inline-block border border-primary px-8 py-3 text-sm font-medium uppercase tracking-wider text-primary transition-colors hover:bg-primary/90"
							>
								Contact Us
							</a>
						</div>
					</section>
				</div>

				<aside className="hidden lg:sticky lg:top-32 lg:block lg:self-start">
					<div className="max-h-[calc(100vh-9rem)] overflow-y-auto pr-1">
						<QuickLinksPanel />
					</div>
				</aside>
			</div>
		</div>
	)
}
